import { sendDataLayer } from "../ecommerce/ecommerce";
import { getTranslations } from "../../translations";
import { resetLoaderButton } from "../../utilities/loaderIconButton";

declare const dataLayer: Array<Record<string, unknown>>;

export class RmaProduct
{
    private element: HTMLElement;
    private checkbox: HTMLInputElement | null;
    private reasonSelect: HTMLSelectElement | null;
    private washedRadios: NodeListOf<HTMLInputElement>;

    constructor(element: HTMLElement)
    {
        this.element = element;

        this.checkbox = this.element.querySelector<HTMLInputElement>(
            ".js-rma-product-checkbox"
        );

        this.reasonSelect = this.element.querySelector<HTMLSelectElement>(
            'select[name^="reasonnumber_"]'
        );

        this.washedRadios = this.element.querySelectorAll<HTMLInputElement>(
            'input[type="radio"][name^="return_washed_"]'
        );

        this.init();
    }

    private init(): void
    {
        if (this.checkbox == null)
            return;

        this.setState();
        this.attachEvents();

        RmaProduct.updateSubmitButton(null);
    }

    private attachEvents(): void
    {
        this.checkbox.addEventListener(
            "change",
            this.onFieldChange
        );

        this.reasonSelect?.addEventListener(
            "change",
            this.onFieldChange
        );

        this.washedRadios.forEach((radio) =>
        {
            radio.addEventListener(
                "change",
                this.onFieldChange
            );
        });
    }

    private onFieldChange = (event): void =>
    {
        this.setState();

        RmaProduct.updateSubmitButton(event.target.id);
    };

    private setState(): void
    {
        if (this.checkbox == null)
            return;

        this.element.classList.toggle(
            "c-rma-product--selected",
            this.checkbox.checked
        );
    }

    private static updateSubmitButton(item): void
    {
        if (item == null || typeof(undefined) == typeof(item))
            return;

        var suffix = $(`#${item}`).closest('.c-rma__order').data('id');

        if (suffix == null || typeof (undefined) == typeof (suffix))
            return;

        const submitButton = document.querySelector<HTMLButtonElement>(
            `#submit_rma-${suffix}`
        );

        if (submitButton == null)
            return;

        const selectedProducts = Array.from(
            document.querySelectorAll<HTMLElement>(
                `#c-rma__order-${suffix} .js-rma-product`
            )
        ).filter((product) =>
        {
            const checkbox = product.querySelector<HTMLInputElement>(
                `#c-rma__order-${suffix} .js-rma-product-checkbox`
            );

            return checkbox?.checked === true;
        });

        const selectedCount = selectedProducts.length;

        const allSelectedProductsValid = selectedProducts.every(
            (product) =>
            {
                const reasonSelect = product.querySelector<HTMLSelectElement>(
                    'select[name^="reasonnumber_"]'
                );

                const washedRadios = product.querySelectorAll<HTMLInputElement>(
                    'input[type="radio"][name^="return_washed_"]'
                );

                const hasReason =
                    reasonSelect != null &&
                    reasonSelect.value !== "" &&
                    reasonSelect.value !== "0";

                const hasWashedOption =
                    Array.from(washedRadios)
                        .some((radio) => radio.checked);

                return hasReason && hasWashedOption;
            }
        );

        const canSubmit =
            selectedCount > 0 &&
            allSelectedProductsValid;

        submitButton.disabled = !canSubmit;

        if (selectedCount === 0)
        {
            submitButton.textContent =
                "Artikel retour aanmelden";

            return;
        }

        if (selectedCount === 1)
        {
            submitButton.textContent =
                "1 artikel retour melden";

            return;
        }

        submitButton.textContent =
            `${selectedCount} artikelen retour melden`;
    }
}

export class RmaSearch
{
    private element: HTMLElement;
    private form: HTMLFormElement | null;
    private input: HTMLInputElement | null;
    private errorContainer: HTMLElement | null;
    private submitButton: HTMLButtonElement | null;

    private readonly referenceRegex =
        /^[A-Za-z]{2}[0-9]{7}$/;

    private readonly orderNumRegex =
        /^8[0-9]{7}$/;

    private readonly errorMsg =
        "Het opgegeven nummer is helaas niet gevonden. Zie de voorbeelden hierboven voor het juiste aantal letters en/of cijfers.";

    constructor(element: HTMLElement)
    {
        this.element = element;

        this.form = this.element.querySelector<HTMLFormElement>(
            ".js-rma-search-form"
        );

        this.input = this.element.querySelector<HTMLInputElement>(
            ".js-rma-search-input"
        );

        this.errorContainer = this.element.querySelector<HTMLElement>(
            ".js-rma-search-errors"
        );

        this.submitButton = this.element.querySelector<HTMLButtonElement>(
            ".js-rma-search-submit"
        );

        this.init();
    }

    private init(): void
    {
        this.sendExistingErrors();
        this.attachEvents();
    }

    private attachEvents(): void
    {
        this.form?.addEventListener(
            "submit",
            this.onSubmit
        );
    }

    private onSubmit = (event: SubmitEvent): void =>
    {
        this.clearErrors();

        const value =
            this.input?.value.trim() ?? "";

        if (!this.isValidOrderNumber(value))
        {
            event.preventDefault();
            event.stopPropagation();

            this.showError(this.errorMsg);
            this.sendError(this.errorMsg);

            this.input?.focus();

            return;
        }
    };

    private isValidOrderNumber(value: string): boolean
    {
        if (value.length === 0)
            return false;

        return (
            this.referenceRegex.test(value) ||
            this.orderNumRegex.test(value)
        );
    }

    private clearErrors(): void
    {
        if (this.errorContainer == null)
            return;

        this.errorContainer.innerHTML = "";
    }

    private showError(message: string): void
    {
        if (this.errorContainer == null)
            return;

        const error = document.createElement("div");

        error.classList.add(
            "c-rma-search__error",
            "js-rma-search-error",
            "error"
        );

        error.textContent = message;

        this.errorContainer.appendChild(error);
    }

    private sendExistingErrors(): void
    {
        const errors =
            this.element.querySelectorAll<HTMLElement>(
                ".js-rma-search-error"
            );

        errors.forEach((error) =>
        {
            if (!this.isVisible(error))
                return;

            const errorMessage =
                error.textContent?.trim();

            if (!errorMessage)
                return;

            this.sendError(errorMessage);
        });
    }

    private sendError(message: string): void
    {
        if (typeof dataLayer === "undefined")
            return;

        sendDataLayer({
            event: "Error",
            category: "Error",
            action: document.location.pathname,
            label: message
        });
    }

    private isVisible(element: HTMLElement): boolean
    {
        return (
            element.offsetWidth > 0 ||
            element.offsetHeight > 0 ||
            element.getClientRects().length > 0
        );
    }
}

export function initRmaProducts(): void
{
    document
        .querySelectorAll<HTMLElement>(
            ".js-rma-product"
        )
        .forEach((element) =>
        {
            new RmaProduct(element);
        });
}

export function initRmaSearch(): void
{
    document
        .querySelectorAll<HTMLElement>(
            ".js-rma-search"
        )
        .forEach((element) =>
        {
            new RmaSearch(element);
        });
}

function initRma(): void
{
    initRmaProducts();
    initRmaSearch();
}

const referenceRegex = /^[A-Za-z]{2}[0-9]{7}$/;
const orderNumRegex = /^8[0-9]{7}$/;

function checkOrderNum(event)
{
    // Clear errors
    $(".error-container").html("");

    let input = $("#txtOrderNumber").val() as string | undefined;
    if (input)
        input = input.trim();

    const matchesReference = input ? referenceRegex.test(input) : false;
    const matchesOrderNum = input ? orderNumRegex.test(input) : false;

    if (!input || (!matchesReference && !matchesOrderNum))
    {
        const ordernumberNotFoundError = getTranslations().rmaOrderNumberNotFoundError;

        // Set new error
        $(".error-container").html(`<div class="error">${ordernumberNotFoundError}</div>`);

        // Send error
        sendDataLayer({
            "event": "Error",
            "category": "Error",
            "action": document.location.pathname,
            "label": ordernumberNotFoundError
        });

        event.preventDefault();
        event.stopPropagation();

        setTimeout(() => {
            resetLoaderButton('#submit_rmasearch');
        }, 50);

        return false;
    }

    return true;
}

if (document.readyState === "loading")
{
    document.addEventListener(
        "DOMContentLoaded",
        initRma
    );
}
else
{
    initRma();
}

window.addEventListener("load", function ()
{
    if ($(".error-container:visible .error").length > 0)
    {
        $(".error-container:visible .error").each(function ()
        {
            const errorMessage = $(this).text();

            // Send error
            sendDataLayer({
                "event": "Error",
                "category": "Error",
                "action": document.location.pathname,
                "label": errorMessage
            });
        });
    }
});

(window as any).RmaProduct = RmaProduct;
(window as any).RmaSearch = RmaSearch;
(window as any).checkOrderNum = checkOrderNum;
